// src/features/dashboard/superadmin/ManageAdminsForm.jsx
import React, { useState, useEffect } from 'react';

export const ManageAdminsForm = ({
  organisation,
  onCancel,
  onSave,
  onUpdateAdmin,
  onDeleteAdmin,
  existingAdmins = [],
  defaultValues = {},
}) => {
  const getDefaultRow = () => ({
    name: '',
    surname: '',
    email: '',
    gender: '',
    school: defaultValues?.school || '',
    province: defaultValues?.province || '',
    district: defaultValues?.district || '',
    circuit: defaultValues?.circuit || '',
  });

  const [newAdmins, setNewAdmins] = useState([getDefaultRow()]);
  const [editingAdmins, setEditingAdmins] = useState(
    existingAdmins.map(admin => ({
      ...admin,
      status: admin.status || 'active', // 👈 ensure status exists
      isEditing: false,
      applyToAll: false
    }))
  );
  const [saving, setSaving] = useState(false);

  // Update newAdmin rows when defaultValues changes
  useEffect(() => {
    setNewAdmins(prev =>
      prev.map(row => ({
        ...row,
        school: row.school || defaultValues?.school || '',
        province: row.province || defaultValues?.province || '',
        district: row.district || defaultValues?.district || '',
        circuit: row.circuit || defaultValues?.circuit || '',
      }))
    );
  }, [defaultValues]);

  // Update editing admins when existingAdmins changes
  useEffect(() => {
    setEditingAdmins(
      existingAdmins.map(admin => ({
        ...admin,
        status: admin.status || 'active',
        isEditing: false,
        applyToAll: false
      }))
    );
  }, [existingAdmins]);

  // ----- New Admin handlers -----
  const addNewAdminRow = () => {
    setNewAdmins([...newAdmins, getDefaultRow()]);
  };

  const removeNewAdminRow = (index) => {
    if (newAdmins.length <= 1) {
      alert('You need at least one row.');
      return;
    }
    const updated = newAdmins.filter((_, i) => i !== index);
    setNewAdmins(updated);
  };

  const updateNewAdmin = (index, field, value) => {
    const updated = [...newAdmins];
    updated[index][field] = value;
    setNewAdmins(updated);
  };

  // ----- Existing Admin handlers -----
  const toggleEditAdmin = (index) => {
    const updated = [...editingAdmins];
    updated[index].isEditing = !updated[index].isEditing;
    setEditingAdmins(updated);
  };

  const updateExistingAdmin = (index, field, value) => {
    const updated = [...editingAdmins];
    updated[index][field] = value;
    setEditingAdmins(updated);
  };

  const saveExistingAdmin = async (index) => {
    const admin = editingAdmins[index];
    if (!admin.name || !admin.surname || !admin.email) {
      alert('Name, Surname, and Email are required.');
      return;
    }
    setSaving(true);
    try {
      // Pass the applyToAll flag and the updated admin (including status) to the parent
      await onUpdateAdmin(organisation, admin, admin.applyToAll);
      const updated = [...editingAdmins];
      updated[index].isEditing = false;
      setEditingAdmins(updated);
      alert('Admin updated successfully!');
    } catch (err) {
      alert('Failed to update admin: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAdmin = async (admin) => {
    if (!window.confirm(`Delete admin ${admin.name} ${admin.surname} (${admin.email})?`)) return;
    setSaving(true);
    try {
      await onDeleteAdmin(organisation, admin);
      // The parent will refresh the list; we don't need to manually update editingAdmins here.
      alert('Admin deleted successfully!');
    } catch (err) {
      alert('Failed to delete admin: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ----- Submit new admins -----
  const handleSubmitNewAdmins = async (e) => {
    e.preventDefault();
    const valid = newAdmins.filter(a => a.name && a.surname && a.email);
    if (valid.length === 0) {
      alert('Please fill in at least one new admin (name, surname, email).');
      return;
    }
    setSaving(true);
    try {
      const adminsWithDefaults = valid.map(a => ({
        ...a,
        school: a.school || 'N/A',
        province: a.province || 'N/A',
        district: a.district || 'N/A',
        circuit: a.circuit || 'N/A',
        status: 'active', // default status for new admins
      }));
      await onSave(organisation, adminsWithDefaults);
      setNewAdmins([getDefaultRow()]);
      alert(`${adminsWithDefaults.length} new admin(s) added successfully!`);
    } catch (err) {
      alert('Failed to add admins: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h2 className="text-2xl font-bold mb-4">Manage Admins for {organisation.organisationName}</h2>

      {/* Existing Admins Section */}
      {editingAdmins.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">Existing Admins</h3>
          <div className="overflow-x-auto">
            <table className="w-full table-auto border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-2 py-1 text-left font-medium">Name</th>
                  <th className="px-2 py-1 text-left font-medium">Surname</th>
                  <th className="px-2 py-1 text-left font-medium">Email</th>
                  <th className="px-2 py-1 text-left font-medium">Gender</th>
                  <th className="px-2 py-1 text-left font-medium">School</th>
                  <th className="px-2 py-1 text-left font-medium">Province</th>
                  <th className="px-2 py-1 text-left font-medium">District</th>
                  <th className="px-2 py-1 text-left font-medium">Circuit</th>
                  <th className="px-2 py-1 text-left font-medium">Status</th> {/* 👈 NEW */}
                  <th className="px-2 py-1 text-center font-medium">Apply to all</th>
                  <th className="px-2 py-1 text-center font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {editingAdmins.map((admin, idx) => (
                  <tr key={admin.userId || idx} className="border-t">
                    <td className="px-2 py-1">
                      {admin.isEditing ? (
                        <input
                          type="text"
                          value={admin.name}
                          onChange={e => updateExistingAdmin(idx, 'name', e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      ) : (
                        <span>{admin.name}</span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {admin.isEditing ? (
                        <input
                          type="text"
                          value={admin.surname}
                          onChange={e => updateExistingAdmin(idx, 'surname', e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      ) : (
                        <span>{admin.surname}</span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {admin.isEditing ? (
                        <input
                          type="email"
                          value={admin.email}
                          onChange={e => updateExistingAdmin(idx, 'email', e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      ) : (
                        <span>{admin.email}</span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {admin.isEditing ? (
                        <select
                          value={admin.gender}
                          onChange={e => updateExistingAdmin(idx, 'gender', e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm"
                        >
                          <option value="">Select</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      ) : (
                        <span>{admin.gender || '-'}</span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {admin.isEditing ? (
                        <input
                          type="text"
                          value={admin.school}
                          onChange={e => updateExistingAdmin(idx, 'school', e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm"
                          placeholder="N/A"
                        />
                      ) : (
                        <span>{admin.school || 'N/A'}</span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {admin.isEditing ? (
                        <input
                          type="text"
                          value={admin.province}
                          onChange={e => updateExistingAdmin(idx, 'province', e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm"
                          placeholder="N/A"
                        />
                      ) : (
                        <span>{admin.province || 'N/A'}</span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {admin.isEditing ? (
                        <input
                          type="text"
                          value={admin.district}
                          onChange={e => updateExistingAdmin(idx, 'district', e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm"
                          placeholder="N/A"
                        />
                      ) : (
                        <span>{admin.district || 'N/A'}</span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {admin.isEditing ? (
                        <input
                          type="text"
                          value={admin.circuit}
                          onChange={e => updateExistingAdmin(idx, 'circuit', e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm"
                          placeholder="N/A"
                        />
                      ) : (
                        <span>{admin.circuit || 'N/A'}</span>
                      )}
                    </td>
                    {/* 👇 NEW: Status column */}
                    <td className="px-2 py-1">
                      {admin.isEditing ? (
                        <select
                          value={admin.status || 'active'}
                          onChange={e => updateExistingAdmin(idx, 'status', e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm"
                        >
                          <option value="active">🟢 Active</option>
                          <option value="partial">🟡 Partial</option>
                          <option value="inactive">🔴 Inactive</option>
                        </select>
                      ) : (
                        <span className="capitalize">{admin.status || 'active'}</span>
                      )}
                    </td>
                    <td className="px-2 py-1 text-center">
                      {admin.isEditing && (
                        <label className="flex items-center justify-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={admin.applyToAll || false}
                            onChange={(e) => {
                              const updated = [...editingAdmins];
                              updated[idx].applyToAll = e.target.checked;
                              setEditingAdmins(updated);
                            }}
                          />
                          <span className="sr-only">Apply to all users</span>
                        </label>
                      )}
                    </td>
                    <td className="px-2 py-1 text-center">
                      {admin.isEditing ? (
                        <>
                          <button
                            onClick={() => saveExistingAdmin(idx)}
                            disabled={saving}
                            className="text-green-600 hover:underline mr-2 text-sm disabled:opacity-50"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => toggleEditAdmin(idx)}
                            className="text-gray-600 hover:underline text-sm"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => toggleEditAdmin(idx)}
                            className="text-blue-600 hover:underline mr-2 text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteAdmin(admin)}
                            disabled={saving}
                            className="text-red-600 hover:underline text-sm disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add New Admins Section */}
      <div className="border-t pt-4">
        <h3 className="text-lg font-semibold mb-2">Add New Admins</h3>
        {defaultValues && (defaultValues.school || defaultValues.province || defaultValues.district || defaultValues.circuit) && (
          <div className="mb-3 text-sm text-gray-600 bg-blue-50 p-2 rounded">
            <span className="font-medium">Defaults from existing admins:</span>
            {defaultValues.school && ` School: ${defaultValues.school}`}
            {defaultValues.province && ` Province: ${defaultValues.province}`}
            {defaultValues.district && ` District: ${defaultValues.district}`}
            {defaultValues.circuit && ` Circuit: ${defaultValues.circuit}`}
          </div>
        )}
        <form onSubmit={handleSubmitNewAdmins} className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full table-auto border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-2 py-1 text-left font-medium">Name</th>
                  <th className="px-2 py-1 text-left font-medium">Surname</th>
                  <th className="px-2 py-1 text-left font-medium">Email</th>
                  <th className="px-2 py-1 text-left font-medium">Gender</th>
                  <th className="px-2 py-1 text-left font-medium">School</th>
                  <th className="px-2 py-1 text-left font-medium">Province</th>
                  <th className="px-2 py-1 text-left font-medium">District</th>
                  <th className="px-2 py-1 text-left font-medium">Circuit</th>
                  <th className="px-2 py-1 text-center font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {newAdmins.map((admin, idx) => (
                  <tr key={idx} className="border-t">
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        value={admin.name}
                        onChange={e => updateNewAdmin(idx, 'name', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="John"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        value={admin.surname}
                        onChange={e => updateNewAdmin(idx, 'surname', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="Doe"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="email"
                        value={admin.email}
                        onChange={e => updateNewAdmin(idx, 'email', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="admin@example.com"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <select
                        value={admin.gender}
                        onChange={e => updateNewAdmin(idx, 'gender', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                      >
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        value={admin.school}
                        onChange={e => updateNewAdmin(idx, 'school', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="N/A"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        value={admin.province}
                        onChange={e => updateNewAdmin(idx, 'province', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="N/A"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        value={admin.district}
                        onChange={e => updateNewAdmin(idx, 'district', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="N/A"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        value={admin.circuit}
                        onChange={e => updateNewAdmin(idx, 'circuit', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="N/A"
                      />
                    </td>
                    <td className="px-2 py-1 text-center">
                      {newAdmins.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeNewAdminRow(idx)}
                          className="text-red-600 hover:underline text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={addNewAdminRow} className="bg-gray-300 text-sm px-2 py-1 rounded hover:bg-gray-400">
              + Add Row
            </button>
            <button type="submit" disabled={saving} className="bg-blue-600 text-white text-sm px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Adding...' : 'Add New Admins'}
            </button>
          </div>
        </form>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400">
          Close
        </button>
      </div>
    </div>
  );
};